// ============================================================
// ACS CONTEXT — Asistente Conversacional Supervisado
// Estado global del módulo ACS + hook useACS()
//
// ARQUITECTURA DE SEGURIDAD:
// - cisoDecision() llama a una Edge Function de Supabase, NO inserta tareas directamente.
//   Esto garantiza que toda creación de tareas pase por lógica de servidor con validación.
// - Todas las mutaciones pasan por Supabase con RLS activo (los usuarios solo modifican
//   lo que les pertenece según su organización y rol).
// - Eventos de auditoría registrados en la tabla audit_trail existente del sistema.
//
// FLUJO PRINCIPAL:
// 1. CISO o referente crea una reunión (createMeeting) vinculada a un control/requisito.
// 2. El participante se une a la sesión Jitsi (joinMeeting) y chatea con la IA (sendSessionMessage).
// 3. Al finalizar, se generan las minutas (generateMinutes) usando Gemini.
// 4. El referente propone tareas (submitProposals) al CISO para su aprobación.
// 5. El CISO aprueba o rechaza cada propuesta (cisoDecision) via Edge Function.
// ============================================================

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { sendACSSessionMessage } from '../lib/acs/acsAI';
import { generateMinutesRawText } from '../lib/acs/acsAI';
import { loadACSPersonaConfig } from '../components/ACS/ACSConfigPanel';
import { buildMinutesPrompt, parseMinutesContent, minutesContentToPartialACSMinutes } from '../lib/acs/minutesSchema';
import type {
  ACSContextType,
  ACSMeeting,
  ACSSessionMessage,
  ACSMinutes,
  ACSTaskProposal,
  ACSAlert,
  ACSItemContext,
  ACSProposedTaskData,
  ACSCisoDecisionInput,
  ACSCisoDecisionOutput,
} from '../types/acs';

// ──────────────────────────────────────────────────────────
// CONTEXT
// ──────────────────────────────────────────────────────────

/** Contexto interno del módulo ACS — accesible solo mediante `useACS()`. */
const ACSContext = createContext<ACSContextType | undefined>(undefined);

// ──────────────────────────────────────────────────────────
// AUDIT HELPER (reutiliza tabla audit_trail existente)
// ──────────────────────────────────────────────────────────

/**
 * Inserta un registro de auditoría en la tabla `audit_trail` del sistema.
 * Las operaciones de auditoría no son bloqueantes — si el INSERT falla,
 * se registra un warning en consola pero la operación principal continúa.
 *
 * @param action - Código de la acción (ej. 'acs.meeting.create', 'acs.message.send').
 * @param entityType - Nombre de la tabla afectada (ej. 'acs_meetings').
 * @param entityId - UUID de la entidad afectada.
 * @param afterState - Datos relevantes del estado posterior a la acción.
 * @param userId - UUID del usuario que realizó la acción.
 * @param organizationId - UUID de la organización del usuario.
 */
async function insertAuditEntry(
  action: string,
  entityType: string,
  entityId: string,
  afterState: Record<string, unknown>,
  userId: string,
  organizationId: string
): Promise<void> {
  try {
    await supabase.from('audit_trail').insert({
      organization_id: organizationId,
      table_name:      entityType,
      record_id:       entityId,
      action,
      user_id:         userId,
      new_values:      afterState,
      description:     `ACS: ${action}`,
    });
  } catch {
    // Audit no debe bloquear la operación principal
    console.warn(`[ACS] ⚠️ Audit insert falló para ${action}`);
  }
}

// ──────────────────────────────────────────────────────────
// TRANSFORMADORES DB → App
// ──────────────────────────────────────────────────────────

/**
 * Transforma una fila de `acs_meetings` (snake_case) a tipo `ACSMeeting` (camelCase).
 * @param row - Registro crudo de Supabase.
 */
function dbMeetingToApp(row: Record<string, unknown>): ACSMeeting {
  return {
    id:             row.id as string,
    organizationId: row.organization_id as string,
    frameworkCode:  row.framework_code as ACSMeeting['frameworkCode'],
    contextType:    row.context_type as ACSMeeting['contextType'],
    contextId:      row.context_id as string,
    contextLabel:   row.context_label as string,
    title:          row.title as string,
    scheduledAt:    row.scheduled_at as string,
    startedAt:      row.started_at as string | undefined,
    endedAt:        row.ended_at as string | undefined,
    status:         row.status as ACSMeeting['status'],
    jitsiRoomName:  row.jitsi_room_name as string,
    aiMode:         row.ai_mode as ACSMeeting['aiMode'],
    createdBy:      row.created_by as string,
    createdAt:      row.created_at as string,
  };
}

/**
 * Transforma una fila de `acs_session_messages` a tipo `ACSSessionMessage`.
 * @param row - Registro crudo de Supabase.
 */
function dbMessageToApp(row: Record<string, unknown>): ACSSessionMessage {
  return {
    id:              row.id as string,
    meetingId:       row.meeting_id as string,
    role:            row.role as 'user' | 'assistant',
    userId:          row.user_id as string | undefined,
    content:         row.content as string,
    citations:       row.citations as ACSSessionMessage['citations'],
    citationsValid:  row.citations_valid as boolean | null,
    messageHash:     row.message_hash as string,
    createdAt:       row.created_at as string,
  };
}

/**
 * Transforma una fila de `acs_task_proposals` a tipo `ACSTaskProposal`.
 * @param row - Registro crudo de Supabase.
 */
function dbProposalToApp(row: Record<string, unknown>): ACSTaskProposal {
  return {
    id:               row.id as string,
    meetingId:        row.meeting_id as string,
    organizationId:   row.organization_id as string,
    frameworkCode:    row.framework_code as ACSTaskProposal['frameworkCode'],
    contextType:      row.context_type as ACSTaskProposal['contextType'],
    contextId:        row.context_id as string,
    title:            row.title as string,
    description:      row.description as string | undefined,
    priority:         row.priority as ACSTaskProposal['priority'],
    dueDate:          row.due_date as string | undefined,
    assigneeId:       row.assignee_id as string | undefined,
    status:           row.status as ACSTaskProposal['status'],
    reviewedBy:       row.reviewed_by as string | undefined,
    reviewedAt:       row.reviewed_at as string | undefined,
    realTaskId:       row.real_task_id as string | undefined,
    approvedSnapshot: row.approved_snapshot as Record<string, unknown> | undefined,
    proposalHash:     row.proposal_hash as string,
    idempotencyKey:   row.idempotency_key as string | undefined,
    notes:            row.notes as string | undefined,
    createdAt:        row.created_at as string,
  };
}

/**
 * Transforma una fila de `acs_alerts` a tipo `ACSAlert`.
 * @param row - Registro crudo de Supabase.
 */
function dbAlertToApp(row: Record<string, unknown>): ACSAlert {
  return {
    id:                  row.id as string,
    organizationId:      row.organization_id as string,
    alertType:           row.alert_type as ACSAlert['alertType'],
    frameworkCode:       row.framework_code as ACSAlert['frameworkCode'],
    contextType:         row.context_type as ACSAlert['contextType'],
    contextId:           row.context_id as string,
    contextLabel:        row.context_label as string,
    severity:            row.severity as ACSAlert['severity'],
    status:              row.status as ACSAlert['status'],
    message:             row.message as string,
    triggerEvent:        row.trigger_event as string | undefined,
    suggestedMeetingId:  row.suggested_meeting_id as string | undefined,
    dismissedBy:         row.dismissed_by as string | undefined,
    dismissedAt:         row.dismissed_at as string | undefined,
    createdAt:           row.created_at as string,
  };
}

/**
 * Transforma una fila de `acs_session_minutes` a tipo `ACSMinutes`.
 * @param row - Registro crudo de Supabase.
 */
function dbMinutesToApp(row: Record<string, unknown>): ACSMinutes {
  return {
    id:               row.id as string,
    meetingId:        row.meeting_id as string,
    status:           row.status as ACSMinutes['status'],
    summary:          row.summary as string | undefined,
    keyDecisions:     row.key_decisions as string[] | undefined,
    actionItems:      row.action_items as string[] | undefined,
    risksIdentified:  row.risks_identified as string[] | undefined,
    proposedTasks:    row.proposed_tasks as ACSMinutes['proposedTasks'],
    rawText:          row.raw_text as string,
    parseError:       row.parse_error as string | undefined,
    generatedBy:      row.generated_by as string,
    generatedAt:      row.generated_at as string,
  };
}

// ──────────────────────────────────────────────────────────
// PROVIDER
// ──────────────────────────────────────────────────────────

/**
 * Proveedor del contexto ACS (Asistente Conversacional Supervisado).
 *
 * Gestiona el ciclo de vida completo de las sesiones ACS:
 * reuniones, mensajes, minutas, propuestas de tareas y alertas.
 *
 * Estado limpiado automáticamente al cerrar sesión (cuando `user.id` se vuelve undefined).
 *
 * @param children - Árbol de componentes React a envolver.
 */
export function ACSProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // ── Estado principal ────────────────────────────────────────────────────────
  const [meetings,        setMeetings]        = useState<ACSMeeting[]>([]);
  const [activeSession,   setActiveSession]   = useState<ACSMeeting | null>(null);
  const [sessionMessages, setSessionMessages] = useState<ACSSessionMessage[]>([]);
  const [proposals,       setProposals]       = useState<ACSTaskProposal[]>([]);
  const [alerts,          setAlerts]          = useState<ACSAlert[]>([]);
  const [isLoading,       setIsLoading]       = useState(false);
  const [error,           setError]           = useState<string | null>(null);

  // ──────────────────────────────────────────────────
  // CARGA INICIAL
  // ──────────────────────────────────────────────────

  /**
   * Carga el historial de reuniones de la organización desde Supabase,
   * ordenadas por fecha programada descendente (más recientes primero).
   */
  const loadMeetings = useCallback(async () => {
    if (!user?.organization_id) return;
    const { data, error: err } = await supabase
      .from('acs_meetings')
      .select('*')
      .eq('organization_id', user.organization_id)
      .order('scheduled_at', { ascending: false });
    if (!err && data) setMeetings(data.map(dbMeetingToApp));
  }, [user?.organization_id]);

  /**
   * Carga las propuestas de tareas de la organización.
   * Incluye propuestas pendientes, aprobadas y rechazadas para trazabilidad.
   */
  const loadProposals = useCallback(async () => {
    if (!user?.organization_id) return;
    const { data, error: err } = await supabase
      .from('acs_task_proposals')
      .select('*')
      .eq('organization_id', user.organization_id)
      .order('created_at', { ascending: false });
    if (!err && data) setProposals(data.map(dbProposalToApp));
  }, [user?.organization_id]);

  /**
   * Carga solo las alertas activas (status = 'active') de la organización.
   * Las alertas descartadas no se incluyen para no saturar la lista.
   */
  const loadAlerts = useCallback(async () => {
    if (!user?.organization_id) return;
    const { data, error: err } = await supabase
      .from('acs_alerts')
      .select('*')
      .eq('organization_id', user.organization_id)
      .eq('status', 'active')
      .order('created_at', { ascending: false });
    if (!err && data) setAlerts(data.map(dbAlertToApp));
  }, [user?.organization_id]);

  /**
   * Recarga en paralelo reuniones, propuestas y alertas.
   * Gestiona estados `isLoading` y `error` durante la operación.
   */
  const refreshData = useCallback(async () => {
    if (!user?.organization_id) return;
    setIsLoading(true);
    setError(null);
    try {
      await Promise.all([loadMeetings(), loadProposals(), loadAlerts()]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error cargando datos ACS';
      setError(msg);
      console.error('[ACS] ❌ refreshData:', err);
    } finally {
      setIsLoading(false);
    }
  }, [loadMeetings, loadProposals, loadAlerts, user?.organization_id]);

  /**
   * Efecto de inicialización y limpieza.
   * - Si el usuario se autentica: carga datos del módulo ACS.
   * - Si el usuario cierra sesión: limpia todo el estado local.
   */
  useEffect(() => {
    if (user?.id) {
      refreshData();
    } else {
      setMeetings([]);
      setProposals([]);
      setAlerts([]);
      setActiveSession(null);
      setSessionMessages([]);
    }
  }, [user?.id]);

  // ──────────────────────────────────────────────────
  // REUNIONES
  // ──────────────────────────────────────────────────

  /**
   * Crea una nueva reunión ACS vinculada a un contexto normativo (control, requisito, artículo).
   *
   * WORKAROUND para RLS:
   * El INSERT con `.select().single()` falla con HTTP 404 (PGRST116) porque la política RLS
   * de `acs_meetings_select` requiere que el usuario sea participante de la reunión, pero
   * el participante se inserta DESPUÉS del INSERT de la reunión. Por eso:
   * 1. Se pre-genera el UUID en cliente (`crypto.randomUUID()`).
   * 2. Se hace INSERT sin `.select()`.
   * 3. Se agrega el creador como participante en `acs_meeting_participants`.
   * 4. Se hace el SELECT (ahora RLS pasa porque el usuario es participante o admin).
   *
   * @param context - Contexto normativo: framework, tipo de entidad, ID y etiqueta.
   * @param scheduledAt - ISO timestamp de la fecha programada de la reunión.
   * @param title - Título descriptivo de la reunión.
   * @param assigneeUserId - UUID opcional del referente técnico asignado (agregado como counterpart).
   * @returns La reunión creada con todos sus datos.
   * @throws Error si hay problemas de autenticación o al insertar en la BD.
   */
  const createMeeting = useCallback(async (
    context: ACSItemContext,
    scheduledAt: string,
    title: string,
    assigneeUserId?: string
  ): Promise<ACSMeeting> => {
    if (!user?.id || !user.organization_id) throw new Error('Usuario no autenticado');

    // Pre-generar UUID en cliente para evitar dependencia en SELECT after INSERT.
    // El INSERT con .select().single() falla con HTTP 404 (PGRST116) porque RLS de
    // acs_meetings_select requiere al usuario en acs_meeting_participants, que aún
    // no existe en el momento del INSERT.
    const meetingId = crypto.randomUUID();
    const participantRole = user.role === 'admin' ? 'ciso' : 'counterpart';

    // 1. INSERT sin .select() (evita PGRST116 por RLS)
    const { error: insertErr } = await supabase
      .from('acs_meetings')
      .insert({
        id:               meetingId,
        organization_id:  user.organization_id,
        framework_code:   context.frameworkCode,
        context_type:     context.contextType,
        context_id:       context.contextId,
        context_label:    context.contextLabel,
        title,
        scheduled_at:     scheduledAt,
        created_by:       user.id,
        status:           'scheduled',
        ai_mode:          'client',
      });

    if (insertErr) throw new Error(insertErr.message ?? 'Error creando reunión ACS');

    // 2. Agregar creador como participante (antes del SELECT para que RLS permita leer)
    const { error: participantErr } = await supabase.from('acs_meeting_participants').insert({
      meeting_id: meetingId,
      user_id:    user.id,
      role:       participantRole,
    });
    if (participantErr) throw new Error(`Error registrando participante: ${participantErr.message}`);

    // 2b. Agregar referente técnico asignado como counterpart (si se especificó y es distinto al creador)
    if (assigneeUserId && assigneeUserId !== user.id) {
      const { error: assigneeErr } = await supabase.from('acs_meeting_participants').insert({
        meeting_id: meetingId,
        user_id:    assigneeUserId,
        role:       'counterpart',
      });
      if (assigneeErr) console.warn('[ACS] ⚠️ Error registrando participante asignado:', assigneeErr.message);
    }

    // 3. Leer la reunión (RLS ahora pasa: usuario es participante o es admin)
    const { data: meetingRow, error: fetchErr } = await supabase
      .from('acs_meetings')
      .select('*')
      .eq('id', meetingId)
      .single();

    if (fetchErr || !meetingRow) throw new Error(fetchErr?.message ?? 'Error obteniendo datos de la reunión');

    const meeting = dbMeetingToApp(meetingRow as Record<string, unknown>);
    setMeetings(prev => [meeting, ...prev]);

    await insertAuditEntry(
      'acs.meeting.create',
      'acs_meetings',
      meeting.id,
      { title, framework: context.frameworkCode, context_label: context.contextLabel },
      user.id,
      user.organization_id
    );

    console.log(`[ACS] ✅ Reunión creada: ${meeting.id}`);
    return meeting;
  }, [user]);

  /**
   * Une al usuario actual a una reunión existente.
   *
   * Si la reunión estaba en estado 'scheduled', la transiciona a 'in_progress'
   * y registra el `started_at`. Registra también el `joined_at` del participante
   * usando upsert para manejar el caso en que el usuario se desconecta y vuelve a unirse.
   *
   * Carga el historial de mensajes previos de la sesión para que el chat
   * tenga contexto completo desde el inicio.
   *
   * @param meetingId - UUID de la reunión a la que unirse.
   * @throws Error si el usuario no está autenticado o la reunión no se encuentra en el estado local.
   */
  const joinMeeting = useCallback(async (meetingId: string): Promise<void> => {
    if (!user?.id || !user.organization_id) throw new Error('Usuario no autenticado');

    const meeting = meetings.find(m => m.id === meetingId);
    if (!meeting) throw new Error('Reunión no encontrada');

    // Transicionar a 'in_progress' si es la primera unión a la reunión
    if (meeting.status === 'scheduled') {
      await supabase
        .from('acs_meetings')
        .update({ status: 'in_progress', started_at: new Date().toISOString() })
        .eq('id', meetingId);

      setMeetings(prev =>
        prev.map(m => m.id === meetingId
          ? { ...m, status: 'in_progress', startedAt: new Date().toISOString() }
          : m
        )
      );
    }

    // Registrar joined_at del participante (upsert por si ya existe el registro)
    await supabase
      .from('acs_meeting_participants')
      .upsert({
        meeting_id: meetingId,
        user_id:    user.id,
        role:       user.role === 'admin' ? 'ciso' : 'counterpart',
        joined_at:  new Date().toISOString(),
      }, { onConflict: 'meeting_id,user_id' });

    // Cargar historial completo de mensajes de la sesión para el chat
    const { data: messages } = await supabase
      .from('acs_session_messages')
      .select('*')
      .eq('meeting_id', meetingId)
      .order('created_at', { ascending: true });

    if (messages) setSessionMessages(messages.map(dbMessageToApp));

    const updatedMeeting = meetings.find(m => m.id === meetingId) ?? meeting;
    setActiveSession(updatedMeeting);

    await insertAuditEntry(
      'acs.meeting.join',
      'acs_meetings',
      meetingId,
      { user_id: user.id, role: user.role },
      user.id,
      user.organization_id
    );

    console.log(`[ACS] ✅ Unido a reunión: ${meetingId}`);
  }, [user, meetings]);

  /**
   * Finaliza una sesión activa: cambia el estado de la reunión a 'completed',
   * registra `ended_at` y actualiza `left_at` del participante actual.
   *
   * @param meetingId - UUID de la reunión a finalizar.
   * @throws Error si el usuario no está autenticado.
   */
  const endSession = useCallback(async (meetingId: string): Promise<void> => {
    if (!user?.id || !user.organization_id) throw new Error('Usuario no autenticado');

    await supabase
      .from('acs_meetings')
      .update({ status: 'completed', ended_at: new Date().toISOString() })
      .eq('id', meetingId);

    await supabase
      .from('acs_meeting_participants')
      .update({ left_at: new Date().toISOString() })
      .eq('meeting_id', meetingId)
      .eq('user_id', user.id);

    setMeetings(prev =>
      prev.map(m => m.id === meetingId
        ? { ...m, status: 'completed', endedAt: new Date().toISOString() }
        : m
      )
    );

    console.log(`[ACS] ✅ Sesión finalizada: ${meetingId}`);
  }, [user]);

  /**
   * Cancela una reunión programada o en progreso.
   * Solo actualiza el estado en BD y en el estado local; no limpia la sesión activa.
   *
   * @param meetingId - UUID de la reunión a cancelar.
   * @throws Error si el usuario no está autenticado.
   */
  const cancelMeeting = useCallback(async (meetingId: string): Promise<void> => {
    if (!user?.id) throw new Error('Usuario no autenticado');

    await supabase
      .from('acs_meetings')
      .update({ status: 'cancelled' })
      .eq('id', meetingId);

    setMeetings(prev =>
      prev.map(m => m.id === meetingId ? { ...m, status: 'cancelled' } : m)
    );
  }, [user]);

  // ──────────────────────────────────────────────────
  // MENSAJES DE SESIÓN
  // ──────────────────────────────────────────────────

  /**
   * Envía un mensaje del usuario al asistente IA y persiste ambos mensajes en Supabase.
   *
   * Flujo:
   * 1. Inserta el mensaje del usuario en `acs_session_messages`.
   * 2. Registra evento de auditoría.
   * 3. Llama a `sendACSSessionMessage` (Gemini) con el historial actual y configuración de persona.
   * 4. Inserta la respuesta del asistente con sus citas y flag de validez.
   *
   * Si la IA falla, inserta un mensaje de error en lugar de lanzar excepción,
   * para no interrumpir la sesión del usuario.
   *
   * @param meetingId - UUID de la reunión activa.
   * @param message - Texto del mensaje del usuario.
   * @throws Error si el usuario no está autenticado, no hay sesión activa o el INSERT falla.
   */
  const sendSessionMessage = useCallback(async (
    meetingId: string,
    message: string
  ): Promise<void> => {
    if (!user?.id || !user.organization_id) throw new Error('Usuario no autenticado');
    if (!activeSession) throw new Error('No hay sesión activa');

    // 1. Insertar mensaje del usuario en BD y actualizar estado local
    const { data: userMsg, error: msgInsertErr } = await supabase
      .from('acs_session_messages')
      .insert({
        meeting_id:      meetingId,
        organization_id: user.organization_id,
        role:            'user',
        user_id:         user.id,
        content:         message,
      })
      .select()
      .single();

    if (msgInsertErr || !userMsg) {
      throw new Error(msgInsertErr?.message ?? 'Error guardando mensaje del usuario');
    }

    setSessionMessages(prev => [...prev, dbMessageToApp(userMsg as Record<string, unknown>)]);

    await insertAuditEntry(
      'acs.message.send',
      'acs_session_messages',
      userMsg.id,
      { role: 'user', meeting_id: meetingId },
      user.id,
      user.organization_id
    );

    // 2. Obtener respuesta IA con validación de citas
    let aiContent: string;
    let aiCitations: ACSSessionMessage['citations'];
    let citationsValid: boolean;

    try {
      // Cargar instrucción personalizada del asistente desde la configuración de la organización
      let customInstruction: string | undefined;
      try {
        customInstruction = loadACSPersonaConfig(user.organization_id).customInstruction || undefined;
      } catch { /* Si falla, continuar sin instrucción personalizada */ }

      const aiResult = await sendACSSessionMessage({
        message,
        meetingId,
        frameworkCode:  activeSession.frameworkCode,
        contextLabel:   activeSession.contextLabel,
        sessionHistory: sessionMessages,
        organizationId: user.organization_id,
        customInstruction,
      });

      aiContent      = aiResult.content;
      aiCitations    = aiResult.citations;
      citationsValid = aiResult.citationsValid;
    } catch (err: unknown) {
      // No lanzar error — insertar mensaje de fallo para no romper la sesión
      aiContent      = 'Error al procesar la consulta. Por favor, inténtelo nuevamente.';
      aiCitations    = undefined;
      citationsValid = false;
      console.error('[ACS] ❌ Error en sendACSSessionMessage:', err);
    }

    // 3. Insertar respuesta del asistente en BD
    const { data: assistantMsg } = await supabase
      .from('acs_session_messages')
      .insert({
        meeting_id:      meetingId,
        organization_id: user.organization_id,
        role:            'assistant',
        user_id:         null,
        content:         aiContent,
        citations:       aiCitations ?? null,
        citations_valid: citationsValid,
      })
      .select()
      .single();

    if (assistantMsg) {
      setSessionMessages(prev => [...prev, dbMessageToApp(assistantMsg as Record<string, unknown>)]);
    }
  }, [user, activeSession, sessionMessages]);

  // ──────────────────────────────────────────────────
  // GENERACIÓN DE MINUTAS
  // ──────────────────────────────────────────────────

  /**
   * Genera las minutas de una reunión completada usando Gemini y el esquema Zod de minutas.
   *
   * Proceso:
   * 1. Carga todos los mensajes de la sesión desde Supabase.
   * 2. Construye el prompt de minutas con `buildMinutesPrompt`.
   * 3. Llama a Gemini para generar el texto raw de las minutas.
   * 4. Parsea el texto con el esquema Zod (`parseMinutesContent`).
   * 5. Guarda en `acs_session_minutes` con upsert (permite regenerar si hubo errores).
   *    - Si el parseo es exitoso → status = 'final'.
   *    - Si el parseo falla → status = 'needs_review' (el CISO debe revisar manualmente).
   *
   * @param meetingId - UUID de la reunión cuyas minutas se van a generar.
   * @returns Las minutas generadas con su estado de parseo.
   * @throws Error si el usuario no está autenticado, la reunión no existe o el upsert falla.
   */
  const generateMinutes = useCallback(async (meetingId: string): Promise<ACSMinutes> => {
    if (!user?.id || !user.organization_id) throw new Error('Usuario no autenticado');

    const meeting = meetings.find(m => m.id === meetingId);
    if (!meeting) throw new Error('Reunión no encontrada');

    // Cargar mensajes de la sesión para el contexto del prompt
    const { data: msgs } = await supabase
      .from('acs_session_messages')
      .select('role, content')
      .eq('meeting_id', meetingId)
      .order('created_at', { ascending: true });

    const messages = (msgs ?? []) as Array<{ role: string; content: string }>;

    const prompt = buildMinutesPrompt(
      meeting.contextLabel,
      meeting.frameworkCode,
      messages
    );

    // Generar texto raw con Gemini
    let rawText = '';
    try {
      rawText = await generateMinutesRawText(prompt);
    } catch (err: unknown) {
      rawText = `Error al generar minuta automática: ${err instanceof Error ? err.message : String(err)}`;
    }

    // Validar y parsear con el esquema Zod de minutas
    const parseResult = parseMinutesContent(rawText);
    const minutesStatus = parseResult.ok ? 'final' : 'needs_review';
    const partialData   = parseResult.ok ? minutesContentToPartialACSMinutes(parseResult.data!) : {};

    // Upsert para permitir regeneración si el primer intento tuvo errores de parseo
    const { data: minutesRow } = await supabase
      .from('acs_session_minutes')
      .upsert({
        meeting_id:      meetingId,
        organization_id: user.organization_id,
        status:          minutesStatus,
        raw_text:        rawText,
        parse_error:     parseResult.error ?? null,
        generated_by:    user.id,
        generated_at:    new Date().toISOString(),
        ...(parseResult.ok ? {
          summary:          partialData.summary,
          key_decisions:    partialData.keyDecisions,
          action_items:     partialData.actionItems,
          risks_identified: partialData.risksIdentified,
          proposed_tasks:   partialData.proposedTasks,
        } : {}),
      }, { onConflict: 'meeting_id' })
      .select()
      .single();

    if (!minutesRow) throw new Error('Error guardando minuta');

    const minutes = dbMinutesToApp(minutesRow as Record<string, unknown>);

    await insertAuditEntry(
      'acs.minutes.generate',
      'acs_session_minutes',
      minutes.id,
      { status: minutesStatus, meeting_id: meetingId },
      user.id,
      user.organization_id
    );

    console.log(`[ACS] ✅ Minuta generada (${minutesStatus}): ${minutes.id}`);
    return minutes;
  }, [user, meetings]);

  // ──────────────────────────────────────────────────
  // PROPUESTAS
  // ──────────────────────────────────────────────────

  /**
   * Envía una lista de propuestas de tareas al CISO para su revisión.
   * Las propuestas quedan en estado 'pending_review' hasta que el CISO tome una decisión.
   *
   * Cada propuesta hereda el contexto normativo de la reunión (frameworkCode, contextType, contextId).
   *
   * @param meetingId - UUID de la reunión desde la que se originan las propuestas.
   * @param proposalsList - Lista de tareas propuestas con título, descripción, prioridad y fecha.
   * @throws Error si el usuario no está autenticado, la reunión no existe o el INSERT falla.
   */
  const submitProposals = useCallback(async (
    meetingId: string,
    proposalsList: ACSProposedTaskData[]
  ): Promise<void> => {
    if (!user?.id || !user.organization_id) throw new Error('Usuario no autenticado');

    const meeting = meetings.find(m => m.id === meetingId);
    if (!meeting) throw new Error('Reunión no encontrada');

    const rows = proposalsList.map(p => ({
      meeting_id:      meetingId,
      organization_id: user.organization_id,
      framework_code:  meeting.frameworkCode,
      context_type:    meeting.contextType,
      context_id:      meeting.contextId,
      title:           p.title,
      description:     p.description,
      priority:        p.priority,
      due_date:        p.estimatedDueDate ?? null,
      assignee_id:     p.suggestedAssigneeId ?? null,
      status:          'pending_review',
    }));

    const { data: newProposals, error: err } = await supabase
      .from('acs_task_proposals')
      .insert(rows)
      .select();

    if (err) throw new Error(err.message);

    if (newProposals) {
      setProposals(prev => [
        ...newProposals.map(p => dbProposalToApp(p as Record<string, unknown>)),
        ...prev,
      ]);
    }

    await insertAuditEntry(
      'acs.proposal.submit',
      'acs_meetings',
      meetingId,
      { proposal_count: proposalsList.length, meeting_id: meetingId },
      user.id,
      user.organization_id
    );

    console.log(`[ACS] ✅ ${proposalsList.length} propuestas enviadas al CISO`);
  }, [user, meetings]);

  // ──────────────────────────────────────────────────
  // DECISIÓN CISO — llama Edge Function (NO inserta tareas directamente)
  // ──────────────────────────────────────────────────

  /**
   * Ejecuta la decisión del CISO sobre una propuesta de tarea (aprobar o rechazar).
   *
   * SEGURIDAD: Esta función llama a la Edge Function `acs_ciso_decision` en Supabase,
   * que se ejecuta en el servidor con service_role. Esto garantiza que:
   * - Solo el CISO (role = 'admin') puede aprobar propuestas.
   * - La creación de la tarea real ocurre en el servidor con validaciones adicionales.
   * - Se usa una `idempotencyKey` para prevenir doble aprobación si hay problemas de red.
   *
   * @param proposalId - UUID de la propuesta sobre la que se decide.
   * @param decision - 'approve' para aprobar y crear la tarea real; 'reject' para rechazar.
   * @param notes - Notas opcionales del CISO sobre la decisión.
   * @throws Error si el usuario no es admin, no está autenticado, o la Edge Function falla.
   */
  const cisoDecision = useCallback(async (
    proposalId: string,
    decision: 'approve' | 'reject',
    notes?: string
  ): Promise<void> => {
    if (!user?.id) throw new Error('Usuario no autenticado');
    if (user.role !== 'admin') throw new Error('Solo el CISO puede tomar decisiones sobre propuestas');

    const idempotencyKey = crypto.randomUUID();

    const input: ACSCisoDecisionInput = {
      proposalId,
      decision,
      notes,
      idempotencyKey,
    };

    const { data, error: fnErr } = await supabase.functions.invoke<ACSCisoDecisionOutput>(
      'acs_ciso_decision',
      { body: input }
    );

    if (fnErr || !data?.success) {
      const errMsg = (data as { success: false; error: string } | undefined)?.error
        ?? fnErr?.message
        ?? 'Error en la decisión CISO';
      throw new Error(errMsg);
    }

    // Actualizar estado local con el resultado de la Edge Function
    setProposals(prev =>
      prev.map(p => p.id === proposalId
        ? {
            ...p,
            status:       decision === 'approve' ? 'approved' : 'rejected',
            reviewedBy:   user.id,
            reviewedAt:   new Date().toISOString(),
            realTaskId:   data.realTaskId ?? undefined,
            idempotencyKey,
          }
        : p
      )
    );

    console.log(`[ACS] ✅ Decisión CISO: ${decision} para propuesta ${proposalId}`);
  }, [user]);

  // ──────────────────────────────────────────────────
  // ALERTAS
  // ──────────────────────────────────────────────────

  /**
   * Descarta (dismiss) una alerta ACS activa para el usuario actual.
   * La alerta se actualiza a status='dismissed' en BD y se elimina del estado local.
   *
   * @param alertId - UUID de la alerta a descartar.
   * @throws Error si el usuario no está autenticado.
   */
  const dismissAlert = useCallback(async (alertId: string): Promise<void> => {
    if (!user?.id) throw new Error('Usuario no autenticado');

    await supabase
      .from('acs_alerts')
      .update({
        status:       'dismissed',
        dismissed_by: user.id,
        dismissed_at: new Date().toISOString(),
      })
      .eq('id', alertId);

    setAlerts(prev => prev.filter(a => a.id !== alertId));
  }, [user]);

  // ──────────────────────────────────────────────────
  // HELPERS
  // ──────────────────────────────────────────────────

  /**
   * Cierra la sesión activa actual limpiando el estado local del chat.
   * No modifica nada en Supabase (usar `endSession` para eso).
   * Útil cuando el usuario navega fuera de la sesión sin terminarla formalmente.
   */
  const closeSession = useCallback((): void => {
    setActiveSession(null);
    setSessionMessages([]);
  }, []);

  /**
   * Retorna todas las reuniones vinculadas a un contexto específico.
   * Permite mostrar el historial de sesiones de un control/requisito particular.
   *
   * @param contextId - UUID del control, requisito o artículo.
   */
  const getMeetingsByContext = useCallback((contextId: string): ACSMeeting[] => {
    return meetings.filter(m => m.contextId === contextId);
  }, [meetings]);

  /**
   * Retorna el número de propuestas en estado 'pending_review'.
   * Usado para mostrar el badge de notificación en el panel del CISO.
   */
  const getPendingProposalsCount = useCallback((): number => {
    return proposals.filter(p => p.status === 'pending_review').length;
  }, [proposals]);

  // ──────────────────────────────────────────────────
  // CONTEXT VALUE
  // ──────────────────────────────────────────────────

  const value: ACSContextType = {
    meetings,
    activeSession,
    sessionMessages,
    proposals,
    alerts,
    isLoading,
    error,
    createMeeting,
    joinMeeting,
    endSession,
    cancelMeeting,
    sendSessionMessage,
    generateMinutes,
    submitProposals,
    cisoDecision,
    dismissAlert,
    closeSession,
    getMeetingsByContext,
    getPendingProposalsCount,
    refreshData,
  };

  return <ACSContext.Provider value={value}>{children}</ACSContext.Provider>;
}

// ──────────────────────────────────────────────────────────
// HOOK
// ──────────────────────────────────────────────────────────

/**
 * Hook para consumir el contexto ACS desde cualquier componente hijo de `ACSProvider`.
 *
 * @throws Error si se usa fuera de `ACSProvider`.
 * @returns Estado completo y operaciones del módulo ACS.
 *
 * @example
 * const { meetings, createMeeting, activeSession } = useACS();
 * await createMeeting(context, scheduledAt, 'Revisión Control 5.1');
 */
export function useACS(): ACSContextType {
  const ctx = useContext(ACSContext);
  if (!ctx) throw new Error('useACS must be used within ACSProvider');
  return ctx;
}
